import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  clearDiamondSettledBets,
  clearStatements,
  fetchDiamondSettledBetsAsync,
  fetchStatementsAsync,
} from "../../store/slices/statementSlice";
import { toast } from "react-toastify";
import { formatDateTimeUtils } from "../../utils/comman";

const firstTruthyId = (...candidates) => {
  for (const v of candidates) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "" && s !== "undefined" && s !== "null") return s;
  }
  return "";
};

const idFromObject = (obj, keys) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return "";
  for (const k of keys) {
    const found = firstTruthyId(obj[k]);
    if (found) return found;
  }
  return "";
};

const MATCH_ID_KEYS = [
  "match_id",
  "matchId",
  "MatchId",
  "sport_event_id",
  "sportEventId",
  "game_id",
  "gameId",
  "fk_match_id",
];

const MARKET_ID_KEYS = [
  "market_id",
  "marketId",
  "MarketId",
  "market_id_str",
  "selection_market_id",
  "fk_market_id",
];

const tryParseJsonIds = (text, matchSoFar, marketSoFar) => {
  let matchId = matchSoFar;
  let marketId = marketSoFar;
  if (typeof text !== "string" || !text.trim()) {
    return { matchId, marketId };
  }
  const trimmed = text.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      const root = Array.isArray(parsed) ? parsed[0] : parsed;
      if (root && typeof root === "object") {
        if (!matchId) matchId = idFromObject(root, MATCH_ID_KEYS);
        if (!marketId) marketId = idFromObject(root, MARKET_ID_KEYS);
        if (
          (!matchId || !marketId) &&
          root.search &&
          typeof root.search === "object"
        ) {
          if (!matchId) matchId = firstTruthyId(root.match_id);
          if (!marketId) marketId = idFromObject(root.search, MARKET_ID_KEYS);
        }
      }
    } catch {
      // fall through to regex
    }
  }
  if (!matchId) {
    const m = trimmed.match(/"match_id"\s*:\s*"([^"]+)"/i);
    if (m) matchId = m[1];
  }
  if (!marketId) {
    const m = trimmed.match(/"market_id"\s*:\s*"([^"]+)"/i);
    if (m) marketId = m[1];
  }
  return { matchId, marketId };
};

const getStatementRowIds = (row) => {
  if (!row) return { matchId: "", marketId: "" };

  const nestedObjects = [
    row,
    row.details,
    row.detail,
    row.bet,
    row.bets?.[0],
    row.transaction,
    row.meta,
    row.extra,
    row.payload,
    row.event,
    row.market,
    row.search,
    row.sport_event,
    row.sportEvent,
    row.data,
    row.ref,
  ].filter((x) => x && typeof x === "object" && !Array.isArray(x));

  let matchId = "";
  let marketId = "";
  for (const obj of nestedObjects) {
    if (!matchId) matchId = idFromObject(obj, MATCH_ID_KEYS);
    if (!marketId) marketId = idFromObject(obj, MARKET_ID_KEYS);
    if (matchId && marketId) break;
  }

  // Account-statement specific: backend sends `event_id` like "35364436_5103916".
  // User asked to send this `event_id` as `market_id` for /bet/diamondSettledBets.
  const rawEventId = firstTruthyId(row?.event_id, row?.eventId, row?.EventId);
  if (!marketId && rawEventId) {
    marketId = rawEventId;
  }
  // If `event_id` contains "<matchId>_<something>", derive match_id from first part.
  if (!matchId && rawEventId && rawEventId.includes("_")) {
    matchId = rawEventId.split("_")[0] || "";
  }

  if (!matchId || !marketId) {
    const stringFields = [
      row.description,
      row.remark,
      row.note,
      row.details,
      row.meta_info,
    ];
    for (const field of stringFields) {
      const next = tryParseJsonIds(field, matchId, marketId);
      matchId = next.matchId || matchId;
      marketId = next.marketId || marketId;
      if (matchId && marketId) break;
    }
  }

  return { matchId, marketId };
};

const formatPlacedAt = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const formatBetType = (bet) => (Number(bet?.is_back) === 1 ? "Back" : "Lay");

const formatBetResult = (bet) => {
  if (!Number(bet?.is_result_declared)) return "Pending";
  const pl = Number(bet?.p_l ?? bet?.profit ?? 0);
  if (pl > 0) return "Won";
  if (pl < 0) return "Lost";
  return "Void";
};

const buildDiamondMarketSummary = (rows, metaRow = {}) => {
  const m = metaRow || {};
  let backSum = 0;
  let laySum = 0;
  for (const r of rows) {
    const pl = Number(r?.p_l ?? r?.profit ?? 0);
    if (Number(r?.is_back) === 1) backSum += pl;
    else laySum += pl;
  }
  const back = m.back_subtotal != null ? Number(m.back_subtotal) : backSum;
  const lay = m.lay_subtotal != null ? Number(m.lay_subtotal) : laySum;
  const market =
    m.market_subtotal != null ? Number(m.market_subtotal) : back + lay;
  const commission =
    m.commission != null
      ? Number(m.commission)
      : Number(m.commission_amount ?? 0);
  const net =
    m.net_market_total != null
      ? Number(m.net_market_total)
      : m.total_profit != null
        ? Number(m.total_profit)
        : market - commission;

  return { back, lay, market, commission, net };
};

const AccountStatement = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { statements, loading, diamondSettledBets } = useSelector(
    (state) => state.statement || {},
  );
  const {
    rows: settledRows,
    metadata: settledMetadata,
    loading: settledLoading,
    error: settledError,
  } = diamondSettledBets || {
    rows: [],
    metadata: [],
    loading: false,
    error: null,
  };

  const [settledModalOpen, setSettledModalOpen] = useState(false);
  /** In-modal message when row has no match/market ids (API shape differs). */
  const [settledModalNotice, setSettledModalNotice] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const userId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("userId");
    return raw ? String(raw).trim() : "";
  }, [location.search]);

  const toApiRange = (fromDateString, toDateString) => {
    if (!fromDateString || !toDateString) return null;
    const from = new Date(fromDateString);
    const to = new Date(toDateString);

    const fromUtc = new Date(
      Date.UTC(
        from.getFullYear(),
        from.getMonth(),
        from.getDate(),
        18,
        30,
        0,
        0,
      ),
    );
    const toUtc = new Date(
      Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 18, 29, 59, 999),
    );

    return {
      from_date: fromUtc.toISOString(),
      to_date: toUtc.toISOString(),
    };
  };

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const formatForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const defaultStart = formatForInput(sevenDaysAgo);
    const defaultEnd = formatForInput(today);

    setStartDate(defaultStart);
    setEndDate(defaultEnd);

    const range = toApiRange(defaultStart, defaultEnd);
    if (range) {
      dispatch(clearStatements());
      const payload = { ...range, limit: 100 };
      if (userId) payload.user_id = userId;
      dispatch(
        fetchStatementsAsync({
          ...payload,
        }),
      );
    }
  }, [dispatch, userId]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const range = toApiRange(startDate, endDate);
    if (!range) {
      toast.error("Please select a valid date range.");
      return;
    }

    dispatch(
      fetchStatementsAsync({
        ...range,
        limit: 100,
      }),
    );
  };

  const rows = useMemo(() => {
    if (!Array.isArray(statements) || !statements.length) return [];
    return statements.map((item, index) => ({
      ...item,
      index: index + 1,
    }));
  }, [statements]);

  const settledSummary = useMemo(
    () =>
      buildDiamondMarketSummary(
        settledRows,
        Array.isArray(settledMetadata) ? settledMetadata[0] : settledMetadata,
      ),
    [settledRows, settledMetadata],
  );

  useEffect(() => {
    if (!settledModalOpen || !settledError) return;
    toast.error(
      typeof settledError === "string"
        ? settledError
        : "Could not load settled bets.",
    );
  }, [settledModalOpen, settledError]);

  const handleViewSettledBets = async (row) => {
    setSettledModalNotice(null);
    dispatch(clearDiamondSettledBets());
    setSettledModalOpen(true);

    const { matchId, marketId } = getStatementRowIds(row);
    if (!matchId || !marketId) {
      setSettledModalNotice("No Data Found");
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.debug(
          "[AccountStatement] row keys for id mapping:",
          Object.keys(row || {}),
          row,
        );
      }
      return;
    }

    try {
      await dispatch(
        fetchDiamondSettledBetsAsync({
          match_id: matchId,
          market_id: marketId,
        }),
      ).unwrap();
    } catch {
      /* toast via useEffect on settledError */
    }
  };

  const closeSettledModal = () => {
    setSettledModalOpen(false);
    setSettledModalNotice(null);
    dispatch(clearDiamondSettledBets());
  };

  return (
    <section className="section accounts">
      <div className="row g-0">
        <div className="col-xl-12">
          <h2 className="userscreen-title">account statement</h2>
          <div className="form_search">
            <form
              noValidate
              className="ng-pristine ng-valid ng-touched"
              onSubmit={handleSubmit}
            >
              <div className="row g-0">
                <div className="col-md-2 col-6">
                  <select className="form-control">
                    <option value="ALL" className="ng-star-inserted">
                      All Sports{" "}
                    </option>
                    <option value="4" className="ng-star-inserted">
                      Cricket{" "}
                    </option>
                    <option value="1" className="ng-star-inserted">
                      Football{" "}
                    </option>
                    <option value="2" className="ng-star-inserted">
                      Tennis{" "}
                    </option>
                    <option value="2378961" className="ng-star-inserted">
                      Politics{" "}
                    </option>
                    <option value="99998" className="ng-star-inserted">
                      Int Casino{" "}
                    </option>
                    <option value="99991" className="ng-star-inserted">
                      Sports book{" "}
                    </option>
                    <option value="7" className="ng-star-inserted">
                      Horse Racing{" "}
                    </option>
                    <option value="4339" className="ng-star-inserted">
                      Greyhound Racing{" "}
                    </option>
                    <option value="99990" className="ng-star-inserted">
                      Binary{" "}
                    </option>
                    <option value="99994" className="ng-star-inserted">
                      Kabaddi{" "}
                    </option>
                    <option value="7522" className="ng-star-inserted">
                      Basketball{" "}
                    </option>
                    <option value="7511" className="ng-star-inserted">
                      Baseball{" "}
                    </option>
                    <option value="20" className="ng-star-inserted">
                      Table Tennis{" "}
                    </option>
                    <option value="998917" className="ng-star-inserted">
                      Volleyball{" "}
                    </option>
                    <option value="7524" className="ng-star-inserted">
                      Ice Hockey{" "}
                    </option>
                    <option value="5" className="ng-star-inserted">
                      Rugby{" "}
                    </option>
                    <option value="26420387" className="ng-star-inserted">
                      Mixed Martial Arts{" "}
                    </option>
                    <option value="3503" className="ng-star-inserted">
                      Darts{" "}
                    </option>
                    <option value="29" className="ng-star-inserted">
                      Futsal{" "}
                    </option>
                  </select>
                </div>
                <div className="col-md-2 col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </div>
                <div className="col-md-2 col-6">
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </div>
                <div className="col-md-2 col-6">
                  <button type="submit" className="btn btn-fs">
                    get statement
                  </button>
                </div>
                <div className="col-md-4 col-12 text-md-end text-center">
                  <div className="document_button"></div>
                </div>
              </div>
            </form>
          </div>
          <div className="table-responsive">
            <table className="datatable table table-striped table-bordered">
              <thead>
                <tr>
                  <th>No</th>
                  <th>date</th>
                  <th>balance</th>
                  <th>D/C</th>
                  <th>Description</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="ng-star-inserted">
                {loading && (
                  <tr>
                    <td colSpan="8" className="noData">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="8" className="noData">
                      No Data Found
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.length > 0 &&
                  rows.map((row) => (
                    <tr key={row.id || row.index}>
                      <td>{row.index}</td>
                      <td>{formatDateTimeUtils(row.created_at ?? row.date)}</td>
                      <td>{row.balance.toFixed(2) ?? "-"}</td>
                      <td>{row.credit_debit ?? "-"}</td>
                      <td>{row.description ?? row.remark ?? "-"}</td>
                      <td>{row.details ?? row.remark ?? "-"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-thmemes"
                          disabled={
                            !row.match_id || !row.event_id || row.remark
                          }
                          title="View settled bets for this line"
                          onClick={() => handleViewSettledBets(row)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {/* <div className="pagination ng-star-inserted">
              <nav aria-label="pagination example" className="Pager1">
                <ul className="pagination pagination-circle justify-content-center">
                  <li className="page-item disabled">
                    <a href="javascript:void(0)" className="page-link">
                      Previous Page
                    </a>
                  </li>
                  <li className="page-item disabled">
                    <a href="javascript:void(0)" className="page-link">
                      Next Page
                    </a>
                  </li>
                </ul>
              </nav>
            </div> */}
          </div>
        </div>
      </div>

      <Modal
        show={settledModalOpen}
        onHide={closeSettledModal}
        size="xl"
        centered
        scrollable
        dialogClassName="bookModal settled-bets-dialog"
      >
        <Modal.Body>
          <h5 className="py-2 px-2 d-flex justify-content-between align-items-center settled-bets-card-title">
            <span>Settled bets</span>
            <button
              type="button"
              className="settled-bets-close-btn"
              aria-label="Close"
              onClick={closeSettledModal}
            >
              <span aria-hidden="true">×</span>
            </button>
          </h5>
          {settledModalNotice && (
            <p className="mb-0 text-center">{settledModalNotice}</p>
          )}
          {!settledModalNotice && settledLoading && (
            <p className="mb-0 text-center">Loading…</p>
          )}
          {!settledModalNotice &&
            !settledLoading &&
            settledRows.length === 0 &&
            !settledError && (
              <p className="mb-0 text-center">No settled bets found.</p>
            )}
          {!settledModalNotice && !settledLoading && settledRows.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="datatable table table-striped table-bordered table-sm mb-3">
                  <thead>
                    <tr>
                      <th>Placed</th>
                      <th>Runner Name</th>
                      <th>Bet ID</th>
                      <th>Type</th>
                      <th>Odds</th>
                      <th>Amount</th>
                      <th>Winnings</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settledRows.map((bet) => (
                      <tr
                        key={
                          bet.bet_id || `${bet.createdAt}-${bet.selection_name}`
                        }
                      >
                        <td>{formatPlacedAt(bet.createdAt)}</td>
                        <td>{bet.selection_name ?? "-"}</td>
                        <td>{bet.bet_id ?? "-"}</td>
                        <td>{formatBetType(bet)}</td>
                        <td>{bet.odds ?? "-"}</td>
                        <td>{bet.stack ?? bet.size ?? "-"}</td>
                        <td>
                          {bet.chips != null
                            ? bet.chips
                            : bet.profit != null
                              ? bet.profit
                              : "-"}
                        </td>
                        <td>{formatBetResult(bet)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="list-unstyled mb-0 small settled-bets-summary">
                <li>
                  Back subtotal:{" "}
                  <strong>{settledSummary.back.toFixed(2)}</strong>
                </li>
                <li>
                  Lay subtotal: <strong>{settledSummary.lay.toFixed(2)}</strong>
                </li>
                <li>
                  Market subtotal:{" "}
                  <strong>{settledSummary.market.toFixed(2)}</strong>
                </li>
                <li>
                  Commission:{" "}
                  <strong>{settledSummary.commission.toFixed(2)}</strong>
                </li>
                <li>
                  NET MARKET TOTAL:{" "}
                  <strong>{settledSummary.net.toFixed(2)}</strong>
                </li>
              </ul>
            </>
          )}
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default AccountStatement;
